import React, { useState } from 'react';
import { X, Clock, ArrowRight, Send, AlertCircle, ArrowUp, Minus, ArrowDown } from 'lucide-react';
import { Task, TaskPriority, TaskStatus, TaskUser, PRIORITY_CONFIG } from '../types';

interface TaskDetailModalProps {
  task: Task;
  users: TaskUser[];
  onClose: () => void;
  onUpdate: (taskId: string, updates: Partial<Task>) => void;
  onAddComment: (taskId: string, text: string) => void;
}

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
];

const STATUS_COLORS: Record<TaskStatus, string> = {
  backlog: 'bg-gray-100 text-gray-700',
  todo: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  done: 'bg-emerald-50 text-emerald-700',
};

const PRIORITY_ICONS: Record<TaskPriority, React.ReactNode> = {
  urgent: <AlertCircle size={13} className='text-red-600' />,
  high: <ArrowUp size={13} className='text-orange-600' />,
  medium: <Minus size={13} className='text-blue-600' />,
  low: <ArrowDown size={13} className='text-gray-400' />,
};

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatStatusLabel(status: TaskStatus) {
  return STATUS_OPTIONS.find(s => s.value === status)?.label ?? status;
}

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  task, users, onClose, onUpdate, onAddComment,
}) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(task.description);
  const [commentText, setCommentText] = useState('');

  const handleTitleSave = () => {
    if (titleDraft.trim() && titleDraft !== task.title) {
      onUpdate(task.id, { title: titleDraft.trim() });
    }
    setEditingTitle(false);
  };

  const handleDescSave = () => {
    onUpdate(task.id, { description: descDraft.trim() });
    setEditingDesc(false);
  };

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    onAddComment(task.id, commentText.trim());
    setCommentText('');
  };

  const priorityCfg = PRIORITY_CONFIG[task.priority];

  const isOverdue = (() => {
    if (!task.dueDate || task.status === 'done') return false;
    const d = new Date(task.dueDate);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return d < today;
  })();

  return (
    /* Bottom sheet on mobile, centered modal on desktop */
    <div className='fixed inset-0 z-50 flex flex-col items-stretch justify-end sm:items-center sm:justify-center bg-black/40 backdrop-blur-sm sm:p-4'>
      <div className='bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-3xl flex flex-col max-h-[92vh] sm:max-h-[90vh]'>

        {/* Header */}
        <div className='flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 flex-shrink-0'>
          <div className='flex items-center gap-2'>
            <span className='text-xs font-mono text-gray-400 font-semibold'>{task.key}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[task.status]}`}>
              {formatStatusLabel(task.status)}
            </span>
          </div>
          <button
            onClick={onClose}
            className='p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors'
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — stacked on mobile, side-by-side on desktop */}
        <div className='flex flex-col sm:flex-row flex-1 overflow-hidden'>

          {/* Metadata panel — compact grid on mobile (shown first), sidebar on desktop (shown last) */}
          <div className='order-first sm:order-last sm:w-56 border-b sm:border-b-0 sm:border-l border-gray-100 bg-gray-50/50 sm:flex-shrink-0 sm:overflow-y-auto'>
            <div className='grid grid-cols-2 sm:grid-cols-1 gap-3 sm:gap-0 px-4 sm:px-5 py-3 sm:py-5 sm:space-y-5'>

              {/* Status */}
              <div>
                <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5'>Status</p>
                <select
                  value={task.status}
                  onChange={e => onUpdate(task.id, { status: e.target.value as TaskStatus })}
                  className='w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white outline-none focus:border-blue-500 transition-colors font-medium'
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div>
                <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5'>Priority</p>
                <div className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border ${priorityCfg.bg} ${priorityCfg.color} ${priorityCfg.border} mb-1.5`}>
                  {PRIORITY_ICONS[task.priority]}
                  {priorityCfg.label}
                </div>
                <select
                  value={task.priority}
                  onChange={e => onUpdate(task.id, { priority: e.target.value as TaskPriority })}
                  className='w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white outline-none focus:border-blue-500 transition-colors'
                >
                  {PRIORITY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Assignee */}
              <div>
                <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5'>Assignee</p>
                <div className='flex items-center gap-2 mb-1.5'>
                  <div className={`w-6 h-6 rounded-full ${task.assignee.avatarColor} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}>
                    {task.assignee.initials}
                  </div>
                  <span className='text-xs text-gray-700 font-medium truncate'>{task.assignee.name}</span>
                </div>
                <select
                  value={task.assignee.id}
                  onChange={e => {
                    const user = users.find(u => u.id === Number(e.target.value));
                    if (user) onUpdate(task.id, { assignee: user });
                  }}
                  className='w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white outline-none focus:border-blue-500 transition-colors'
                >
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              {/* Due date */}
              <div>
                <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5'>Due Date</p>
                <input
                  type='date'
                  value={task.dueDate ?? ''}
                  onChange={e => onUpdate(task.id, { dueDate: e.target.value || null })}
                  className='w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white outline-none focus:border-blue-500 transition-colors'
                />
                {isOverdue && (
                  <p className='text-[10px] font-semibold text-red-600 mt-1 flex items-center gap-1'>
                    <AlertCircle size={10} /> Overdue
                  </p>
                )}
              </div>

              {/* Reporter — desktop only */}
              <div className='hidden sm:block'>
                <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5'>Reporter</p>
                <div className='flex items-center gap-2'>
                  <div className={`w-6 h-6 rounded-full ${task.reporter.avatarColor} flex items-center justify-center text-white text-[10px] font-bold`}>
                    {task.reporter.initials}
                  </div>
                  <span className='text-xs text-gray-700 font-medium truncate'>{task.reporter.name}</span>
                </div>
              </div>

              {/* Created — desktop only */}
              <div className='hidden sm:block'>
                <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5'>Created</p>
                <p className='text-[11px] text-gray-500'>{formatDateTime(task.createdAt)}</p>
              </div>

              {/* Updated — desktop only */}
              <div className='hidden sm:block'>
                <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5'>Updated</p>
                <p className='text-[11px] text-gray-500'>{formatDateTime(task.updatedAt)}</p>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className='flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 space-y-5 sm:space-y-6'>

            {/* Title */}
            {editingTitle ? (
              <input
                value={titleDraft}
                onChange={e => setTitleDraft(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={e => e.key === 'Enter' && handleTitleSave()}
                autoFocus
                className='w-full text-lg font-semibold text-gray-900 border border-blue-400 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-50'
              />
            ) : (
              <h1
                onClick={() => setEditingTitle(true)}
                className='text-base sm:text-lg font-semibold text-gray-900 cursor-text hover:bg-gray-50 rounded-lg px-2 py-1.5 -mx-2 transition-colors'
                title='Click to edit'
              >
                {task.title}
              </h1>
            )}

            {/* Description */}
            <div>
              <p className='text-xs font-semibold text-gray-500 mb-2'>Description</p>
              {editingDesc ? (
                <div>
                  <textarea
                    value={descDraft}
                    onChange={e => setDescDraft(e.target.value)}
                    autoFocus
                    rows={4}
                    className='w-full text-sm border border-blue-400 rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-blue-50 resize-none'
                  />
                  <div className='flex gap-2 mt-2'>
                    <button
                      onClick={handleDescSave}
                      className='px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors'
                    >
                      Save
                    </button>
                    <button
                      onClick={() => { setDescDraft(task.description); setEditingDesc(false); }}
                      className='px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition-colors'
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  onClick={() => setEditingDesc(true)}
                  className={`text-sm cursor-text hover:bg-gray-50 rounded-lg px-2 py-1.5 -mx-2 transition-colors ${task.description ? 'text-gray-700' : 'text-gray-400 italic'}`}
                >
                  {task.description || 'Click to add a description...'}
                </p>
              )}
            </div>

            {/* Labels */}
            {task.labels.length > 0 && (
              <div>
                <p className='text-xs font-semibold text-gray-500 mb-2'>Labels</p>
                <div className='flex flex-wrap gap-1.5'>
                  {task.labels.map(label => (
                    <span key={label} className='text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 font-medium'>
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Activity */}
            <div>
              <p className='text-xs font-semibold text-gray-500 mb-3'>Activity</p>
              <div className='space-y-3'>
                <div className='flex gap-3'>
                  <div className={`w-6 h-6 rounded-full ${task.reporter.avatarColor} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 mt-0.5`}>
                    {task.reporter.initials}
                  </div>
                  <div>
                    <p className='text-xs text-gray-700'>
                      <span className='font-semibold'>{task.reporter.name}</span> created this task
                    </p>
                    <p className='text-[11px] text-gray-400 flex items-center gap-1 mt-0.5'>
                      <Clock size={10} /> {formatDateTime(task.createdAt)}
                    </p>
                  </div>
                </div>

                {task.statusHistory.map((entry, i) => (
                  <div key={i} className='flex gap-3'>
                    <div className={`w-6 h-6 rounded-full ${entry.changedBy.avatarColor} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 mt-0.5`}>
                      {entry.changedBy.initials}
                    </div>
                    <div>
                      <p className='text-xs text-gray-700 flex items-center gap-1 flex-wrap'>
                        <span className='font-semibold'>{entry.changedBy.name}</span>
                        moved from
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[entry.from]}`}>{formatStatusLabel(entry.from)}</span>
                        <ArrowRight size={10} className='text-gray-400' />
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[entry.to]}`}>{formatStatusLabel(entry.to)}</span>
                      </p>
                      {entry.reason && (
                        <div className='mt-1.5 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-2'>
                          <p className='text-[11px] text-amber-800 font-medium mb-0.5'>Deprioritization reason:</p>
                          <p className='text-xs text-amber-700'>{entry.reason}</p>
                        </div>
                      )}
                      <p className='text-[11px] text-gray-400 flex items-center gap-1 mt-0.5'>
                        <Clock size={10} /> {formatDateTime(entry.changedAt)}
                      </p>
                    </div>
                  </div>
                ))}

                {task.comments.map(comment => (
                  <div key={comment.id} className='flex gap-3'>
                    <div className={`w-6 h-6 rounded-full ${comment.author.avatarColor} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 mt-0.5`}>
                      {comment.author.initials}
                    </div>
                    <div className='flex-1'>
                      <p className='text-xs font-semibold text-gray-800'>{comment.author.name}</p>
                      <div className='mt-1 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100'>
                        <p className='text-sm text-gray-700'>{comment.text}</p>
                      </div>
                      <p className='text-[11px] text-gray-400 flex items-center gap-1 mt-0.5'>
                        <Clock size={10} /> {formatDateTime(comment.createdAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Add comment */}
            <form onSubmit={handleCommentSubmit} className='flex gap-2 sm:gap-3'>
              <input
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder='Add a comment...'
                className='flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 placeholder-gray-400'
              />
              <button
                type='submit'
                disabled={!commentText.trim()}
                className='px-3 sm:px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
              >
                <Send size={15} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
